import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

/**
 * Nobody can currently say whether this serving tenant's nodes are healthy.
 *
 * The node-health verdict is `unobserved` — no fresh node list from the agent,
 * an agent that reported none, or EC2 unreadable — and a quiet cluster nobody
 * looked at is not a healthy one. Every node alarm already open for this tenant
 * (`VIBE_TENANT_NODE_UNHEALTHY`, `VIBE_TENANT_GIT_HOST_NODE_UNHEALTHY`) is HELD
 * while this stands, because an unobserved verdict supports no retraction.
 */
export const VIBE_TENANT_NODE_HEALTH_UNOBSERVED = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "The node health of this serving tenant cannot be judged: its agent has not reported a fresh Nomad node list, reported none, or EC2 could not be read for its instances. The payload carries the reason. Any node alarm already open for this tenant is being held, not retracted, until the cluster can be observed again. Nothing is known to be wrong with the nodes — what is missing is the ability to look.",
  severity: "medium",
  remedy:
    "Read the reason on the payload. `no node list` on an agent older than the node reporter is expected until the agent is upgraded. A current agent sending none is silent (its heartbeat has stopped) or failing to list its Nomad nodes (its logs name the error). `EC2 could not be read` is the control plane's `ReadTenantNodeHealth` permission or an AWS throttle — a throttle clears on the next sweep, a permission error persists and names the action. This clears on its own on the first sweep that observes the cluster."
} as const satisfies IssueDefinition;
